import { formatMinor, moveInOrder } from '@didto/domain';
import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  IconButton,
  Loading,
  PageHeader,
  StatusChip,
} from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '@/app/session';
import { useCommand } from '@/app/useCommand';
import type { Category, Product } from '@/lib/catalog';
import {
  catalogKeys,
  fetchAssignments,
  fetchAvailability,
  fetchCategories,
  fetchCosts,
  fetchModifierGroups,
  fetchProducts,
  fetchRoutes,
  fetchVariants,
} from '@/lib/catalog';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { CategorySheet } from './CategorySheet';
import { ProductSheet } from './ProductSheet';
import './catalog.css';

type Filter = 'all' | 'none' | string;

/** Categories and products of the business; variants, modifiers and availability live in the product sheet. */
export function CatalogPage() {
  const s = useSession();
  const canManage = s.can('catalog.manage');
  // ADR-037: reordering is a command; none starts while the server cannot be reached
  const online = useOnline();
  const currency = s.me?.business?.currency ?? 'PHP';
  const locationId = s.me?.active_location_id ?? '';
  const cmd = useCommand([['catalog']]);
  const [filter, setFilter] = useState<Filter>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Product | 'new' | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | 'new' | null>(null);

  const categories = useQuery({ queryKey: catalogKeys.categories, queryFn: fetchCategories });
  const products = useQuery({ queryKey: catalogKeys.products, queryFn: fetchProducts });
  const variants = useQuery({ queryKey: catalogKeys.variants, queryFn: fetchVariants });
  const costs = useQuery({ queryKey: catalogKeys.costs, queryFn: fetchCosts, enabled: canManage });
  const groups = useQuery({ queryKey: catalogKeys.groups, queryFn: fetchModifierGroups });
  const assignments = useQuery({ queryKey: catalogKeys.assignments, queryFn: fetchAssignments });
  const availability = useQuery({ queryKey: catalogKeys.availability, queryFn: fetchAvailability });
  const routes = useQuery({ queryKey: catalogKeys.routes, queryFn: fetchRoutes });
  // every read the list and the sheets stand on: one failing would show a wrong picture
  const reads = [categories, products, variants, costs, groups, assignments, availability, routes];
  const readError = reads.find((q) => q.isError)?.error;

  const liveCategories = (categories.data ?? []).filter((c) => !c.archived_at);
  const allProducts = products.data ?? [];
  const visible = allProducts.filter(
    (p) =>
      (showArchived || !p.archived_at) &&
      (filter === 'all' || (filter === 'none' ? p.category_id === null : p.category_id === filter)),
  );
  const variantsOf = (productId: string) =>
    (variants.data ?? []).filter((v) => v.product_id === productId && !v.archived_at);
  const costMap = new Map((costs.data ?? []).map((c) => [c.product_variant_id, c.cost_minor]));
  // until the categories are read, a product's category is not known yet (not "archived")
  const categoryName = (id: string | null) =>
    !id
      ? 'Uncategorised'
      : !categories.data
        ? '…'
        : (liveCategories.find((c) => c.id === id)?.name ?? 'Archived category');
  /** Where a product's lines are made: the route's name (its code when it has none). */
  const routeName = (id: string | null) => {
    const r = routes.data?.find((x) => x.id === id);
    return r ? r.name || r.code : null;
  };
  const priceLabel = (productId: string): ReactNode => {
    const live = variantsOf(productId);
    if (live.length === 0) return '—';
    if (live.length === 1)
      return <span className="d-mono">{formatMinor(live[0]!.price_minor, currency)}</span>;
    const min = Math.min(...live.map((v) => v.price_minor));
    return (
      <>
        from <span className="d-mono">{formatMinor(min, currency)}</span> · {live.length} variants
      </>
    );
  };
  const soldOut = (productId: string) => {
    const live = variantsOf(productId);
    const off = live.filter((v) =>
      (availability.data ?? []).some(
        (a) => a.location_id === locationId && a.product_variant_id === v.id && !a.is_available,
      ),
    ).length;
    return off === 0 ? null : off === live.length ? 'sold out' : 'partly sold out';
  };
  const selectedCategory = liveCategories.find((c) => c.id === filter) ?? null;

  /** The products a product is reordered among: the live ones of its category. */
  const siblingsOf = (p: Product) =>
    allProducts.filter((x) => !x.archived_at && x.category_id === p.category_id);
  const move = async (p: Product, dir: -1 | 1) => {
    const siblings = siblingsOf(p);
    const ids = moveInOrder(
      siblings.map((x) => x.id),
      siblings.findIndex((x) => x.id === p.id),
      dir,
    );
    if (!ids) return;
    await cmd.mutateAsync({
      kind: 'product.reorder',
      payload: { category_id: p.category_id, ordered_ids: ids },
    });
  };

  return (
    <div className="d-page">
      <PageHeader
        eyebrow="Catalog"
        title="Products"
        subtitle="What the POS sells: categories, products, variants, modifiers, prices and availability."
        actions={
          <>
            <Link to="/catalog/modifiers" className="d-btn d-btn--ghost">
              Modifier groups
            </Link>
            {canManage && s.canActOnOwners && (
              <Link to="/catalog/import" className="d-btn d-btn--ghost" data-testid="import-menu">
                Import menu
              </Link>
            )}
            {canManage && (
              <>
                <Button
                  variant="ghost"
                  onClick={() => setEditingCategory('new')}
                  data-testid="new-category"
                >
                  New category
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setEditing('new')}
                  data-testid="new-product"
                >
                  New product
                </Button>
              </>
            )}
          </>
        }
      />

      <div className="d-spread">
        <div className="d-inline">
          {/* the Pills markup by hand: each category keeps its test id and its colour dot */}
          <div
            className="d-pills"
            role="group"
            aria-label="Filter by category"
            data-testid="category-filter"
          >
            <button
              type="button"
              className="d-pill"
              aria-pressed={filter === 'all'}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            {liveCategories.map((c) => (
              <button
                key={c.id}
                type="button"
                className="d-pill"
                aria-pressed={filter === c.id}
                onClick={() => setFilter(c.id)}
                data-testid={`category-pill-${c.id}`}
              >
                {c.color && <span className="d-dot" style={{ color: c.color }} aria-hidden />}
                {c.name}
              </button>
            ))}
            <button
              type="button"
              className="d-pill"
              aria-pressed={filter === 'none'}
              onClick={() => setFilter('none')}
            >
              Uncategorised
            </button>
          </div>
          {canManage && selectedCategory && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditingCategory(selectedCategory)}
              data-testid="edit-category"
            >
              Edit category
            </Button>
          )}
        </div>
        <Checkbox checked={showArchived} onChange={setShowArchived} label="Show archived" />
      </div>

      {readError && (
        <ErrorState
          title="Could not load the catalog"
          message={errorText(readError)}
          onRetry={() => {
            for (const q of reads) if (q.isError) void q.refetch();
          }}
        />
      )}
      <div className="d-list" data-testid="product-list">
        {products.isLoading && <Loading rows={4} label="Loading products…" />}
        {visible.map((p) => {
          const so = soldOut(p.id);
          const route = routeName(p.production_route_id);
          const siblings = canManage && !p.archived_at ? siblingsOf(p) : [];
          const at = siblings.findIndex((x) => x.id === p.id);
          return (
            <div key={p.id} className="d-row" data-testid={`product-row-${p.id}`}>
              <div className="d-row__main">
                <div className="d-row__title">{p.name}</div>
                <div className="d-row__meta d-inline">
                  <span>
                    {categoryName(p.category_id)} · {priceLabel(p.id)}
                  </span>
                  {p.archived_at && <StatusChip shape="square">archived</StatusChip>}
                  {so && <StatusChip tone="warning">{so}</StatusChip>}
                  {/* where its lines go, in words, once the routes are read; "no kitchen or bar" only
                      for a product that has no route at all (an archived route is not listed) */}
                  {routes.data &&
                    (route ? (
                      <Badge outline testId={`product-route-${p.id}`}>
                        <span aria-hidden>→</span> {route}
                      </Badge>
                    ) : p.production_route_id ? null : (
                      <Badge testId={`product-route-${p.id}`}>no kitchen or bar</Badge>
                    ))}
                </div>
              </div>
              <div className="d-actions">
                {canManage && !p.archived_at && (
                  <>
                    <IconButton
                      size="sm"
                      variant="quiet"
                      label={`move ${p.name} up`}
                      disabled={!online || at <= 0}
                      onClick={() => void move(p, -1)}
                    >
                      ↑
                    </IconButton>
                    <IconButton
                      size="sm"
                      variant="quiet"
                      label={`move ${p.name} down`}
                      disabled={!online || at < 0 || at >= siblings.length - 1}
                      onClick={() => void move(p, 1)}
                    >
                      ↓
                    </IconButton>
                  </>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing(p)}
                  aria-label={`open ${p.name}`}
                >
                  {canManage && !p.archived_at ? 'Edit' : 'Open'}
                </Button>
              </div>
            </div>
          );
        })}
        {products.isSuccess && visible.length === 0 && (
          <EmptyState title={allProducts.length === 0 ? 'No products yet' : 'Nothing here'}>
            {allProducts.length === 0 ? 'Create the first one.' : null}
          </EmptyState>
        )}
      </div>

      {editing && (
        <ProductSheet
          product={editing === 'new' ? null : editing}
          categories={liveCategories}
          routes={routes.data ?? []}
          variants={
            editing === 'new'
              ? []
              : (variants.data ?? []).filter((v) => v.product_id === editing.id)
          }
          costs={costMap}
          groups={(groups.data ?? []).filter((g) => !g.archived_at)}
          assignments={
            editing === 'new'
              ? []
              : (assignments.data ?? []).filter((a) => a.product_id === editing.id)
          }
          availability={availability.data ?? []}
          defaultCategoryId={filter === 'all' || filter === 'none' ? null : filter}
          onClose={() => setEditing(null)}
        />
      )}
      {editingCategory && (
        <CategorySheet
          category={editingCategory === 'new' ? null : editingCategory}
          onClose={() => setEditingCategory(null)}
          onSave={async (v) => {
            if (editingCategory === 'new')
              await cmd.mutateAsync({ kind: 'category.create', payload: v });
            else
              await cmd.mutateAsync({
                kind: 'category.update',
                payload: { category_id: editingCategory.id, ...v },
              });
            setEditingCategory(null);
          }}
          onArchive={
            editingCategory !== 'new' && !editingCategory.archived_at
              ? async () => {
                  await cmd.mutateAsync({
                    kind: 'category.archive',
                    payload: { category_id: editingCategory.id },
                  });
                  setEditingCategory(null);
                  setFilter('all');
                }
              : undefined
          }
        />
      )}
    </div>
  );
}
